import { SupabaseClient } from '@supabase/supabase-js'
import { toast } from "sonner"
import { env } from "next-runtime-env"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'

const logger = createIsomorphicLogger('db.files')

export const uploadFile = async (
	supabase: SupabaseClient,
  file: File,
  payload: {
    name: string
    user_id: string
    file_id: string
  }
) => {
  const filePath = `${payload.user_id}/${Buffer.from(payload.file_id).toString("base64")}`

  const { error } = await supabase.storage
    .from("files")
    .upload(filePath, file, {
      upsert: true
    })

  return { filePath, error }
}

export const deleteFileFromStorage = async (supabase: SupabaseClient, filePath: string) => {
  const { error } = await supabase.storage.from("files").remove([filePath])

  if (error) {
    toast.error("Failed to remove file!")
    return
  }
}

export const getStorageFileUrl = async (supabase: SupabaseClient, filePath: string) => {
  const { data, error } = await supabase.storage
    .from("files")
    .createSignedUrl(filePath, 60 * 60 * 24) // 24hrs

  if (error) {
    logError(logger, error, `Error getting file URL with path: ${filePath}`)
    throw new Error("Error getting file URL")
  }

  return data.signedUrl
}

export const downloadFileFromStorage = async (supabase: SupabaseClient, filePath: string) => {
  const { data, error } = await supabase.storage
    .from("files")
    .download(filePath)

  if (error) {
    logError(logger, error, `Error downloading file with path: ${filePath}`)
    throw new Error("Error downloading file")
  }

  return data
}

export const storageFileExists = async (supabase: SupabaseClient, filePath: string) => {
	try {
		const token = (await supabase.auth.getSession()).data.session?.access_token;
		const NEXT_PUBLIC_SUPABASE_URL = env('NEXT_PUBLIC_SUPABASE_URL')

		if (token) {
			const url = `${NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/files/${filePath}`
			const response = await fetch(url, {
				method: 'HEAD',
				headers: {
					authorization: token
				},
			})
			return { status:`${response.status}`, error:`${response.statusText}` }
		}
		else {
			return { status:400, error:"Bad Request" }
		}
	}
	catch (error: any) {
		return { status:500, error:`${error}` }
  }
}
